import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { publicUrl } from '../crm/publicUrl';
import { trackVisit, trackEvent } from '../crm/visitor';
import ContactModal from '../crm/ContactModal';

const API = '/crm-api';
const SITE = 'https://diabetikfootcareindia.com';
// Bundled PDF. NOT read from admin → Content: those records point at a legacy
// host that is no longer served, so linking them would ship a broken download
// to prospects. Revisit if Content moves to the CRM backend.
// Per-product one-pagers, generated by brochures/build_brochures.py.
// Falls back to the combined catalogue if a product's file isn't there yet.
const BROCHURE = '/DFCI_Product_Brochure.pdf';
const brochureFor = (name) => `/brochures/${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.pdf`;

const DESIGNATIONS = {
  'karthik.dfc@dfcindia.in': 'Sales Manager', 'anitha.dfc@dfcindia.in': 'HR Manager',
  'priya.dfc@dfcindia.in': 'Senior Marketing Executive', 'arjun.dfc@dfcindia.in': 'Senior Marketing Executive',
  'deepa.dfc@dfcindia.in': 'Senior Marketing Executive', 'vignesh.dfc@dfcindia.in': 'Junior Marketing Executive',
  'sowmya.dfc@dfcindia.in': 'Junior Marketing Executive', 'ramesh.dfc@dfcindia.in': 'Junior Marketing Executive',
  'divya.dfc@dfcindia.in': 'Junior Marketing Executive', 'suresh.dfc@dfcindia.in': 'Junior Marketing Executive',
};

const PRODUCTS = [
  { img: '/products/footlab.png', name: 'All-in-One Diabetic Foot Lab', tag: '🩺 Diagnostic',
    desc: 'Complete diabetic foot evaluation in one integrated workstation.',
    features: ['Early Detection', 'Comprehensive Analysis', 'Vibration & Pressure'] },
  { img: '/products/vascular.jpg', name: 'Vascular Screening Devices', tag: '❤️ Vascular',
    desc: 'Non-invasive assessment of peripheral blood-vessel function.',
    features: ['ABI / TBI', 'Circulation Support', 'Non-Invasive'] },
  { img: null, name: 'Neuropathy Screening Devices', tag: '🧠 Neurology',
    desc: 'Accurate, early detection of diabetic peripheral neuropathy.',
    features: ['Nerve Health', 'VPT Testing', 'Pain Management'] },
  { img: '/products/plantar.jpg', name: 'Plantar Pressure Measurement', tag: '👣 Podiatry',
    desc: 'Dynamic foot-pressure mapping to flag high-risk zones.',
    features: ['Pressure Mapping', 'Ulcer Prevention', 'Gait Analysis'] },
  { img: '/products/wound.png', name: 'Pain & Wound Care Devices', tag: '🩹 Therapy',
    desc: 'Therapeutic solutions for effective wound management.',
    features: ['Wound Care', 'Pain Relief', 'Recovery Support'] },
  { img: '/products/podiatry.png', name: 'Podiatry Products', tag: '🦶 Foot Care',
    desc: 'Professional-grade tools for complete clinical foot care.',
    features: ['Clinical Tools', 'Consumables', 'Expert Care'] },
];

// Key achievements. Each carries an icon that means something — a calendar for
// years, a globe for countries, a hospital for clinics, a seal for the certification.
const STATS = [
  { big: '30+', label: 'Years Experience', sub: 'since 1994',
    icon: '<path d="M7 2v3M17 2v3M3 9h18M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z"/>' },
  { big: '30+', label: 'Countries Served', sub: 'across the world',
    icon: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 010 18a15 15 0 010-18z"/>' },
  { big: '500+', label: 'Clinics Served', sub: 'hospitals & practices',
    icon: '<path d="M4 21V8l8-5 8 5v13M9 21v-6h6v6M12 10v3M10.5 11.5h3"/>' },
  { big: 'ISO', label: '13485:2016 Certified', sub: 'medical devices',
    icon: '<path d="M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 7.7l5.4-.8L12 2z"/><path d="M8.5 21l3.5-2 3.5 2"/>' },
];

// [label, href, brand-colour, icon path] — original brand icons + colours.
const SOCIALS = [
  ['LinkedIn', 'https://linkedin.com/company/diabetik-foot-care-india', '#0a66c2', '<path d="M4.98 3.5A2.5 2.5 0 100 3.51a2.5 2.5 0 004.98 0zM.5 8h4V24h-4zM8 8h3.8v2.2h.05C12.4 8.9 14 8 16 8c4 0 5 2.6 5 6V24h-4v-7.5c0-1.8 0-4.1-2.5-4.1s-2.9 2-2.9 4V24H8z"/>'],
  ['Instagram', 'https://instagram.com/diabetikfootcareindia', '#E4405F', '<path d="M12 2.2c3.2 0 3.6.01 4.86.07 1.17.05 1.8.25 2.23.42.56.22.96.48 1.38.9.42.42.68.82.9 1.38.17.42.37 1.06.42 2.23.06 1.27.07 1.65.07 4.86s-.01 3.6-.07 4.86c-.05 1.17-.25 1.8-.42 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.17-1.06.37-2.23.42-1.27.06-1.65.07-4.86.07s-3.6-.01-4.86-.07c-1.17-.05-1.8-.25-2.23-.42a3.7 3.7 0 01-1.38-.9 3.7 3.7 0 01-.9-1.38c-.17-.42-.37-1.06-.42-2.23C2.21 15.6 2.2 15.22 2.2 12s.01-3.6.07-4.86c.05-1.17.25-1.8.42-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.17 1.06-.37 2.23-.42C8.4 2.21 8.78 2.2 12 2.2zm0 1.8c-3.16 0-3.53.01-4.78.07-.9.04-1.38.19-1.7.32-.43.16-.74.36-1.06.68-.32.32-.52.63-.68 1.06-.13.32-.28.8-.32 1.7-.06 1.25-.07 1.62-.07 4.78s.01 3.53.07 4.78c.04.9.19 1.38.32 1.7.16.43.36.74.68 1.06.32.32.63.52 1.06.68.32.13.8.28 1.7.32 1.25.06 1.62.07 4.78.07s3.53-.01 4.78-.07c.9-.04 1.38-.19 1.7-.32.43-.16.74-.36 1.06-.68.32-.32.52-.63.68-1.06.13-.32.28-.8.32-1.7.06-1.25.07-1.62.07-4.78s-.01-3.53-.07-4.78c-.04-.9-.19-1.38-.32-1.7a2.9 2.9 0 00-.68-1.06 2.9 2.9 0 00-1.06-.68c-.32-.13-.8-.28-1.7-.32C15.53 4.01 15.16 4 12 4zm0 3.05A4.95 4.95 0 1112 17a4.95 4.95 0 010-9.9zm0 1.8a3.15 3.15 0 100 6.3 3.15 3.15 0 000-6.3zm5.15-3.2a1.15 1.15 0 110 2.3 1.15 1.15 0 010-2.3z"/>'],
  ['Facebook', 'https://facebook.com/DiabetikFootCareIndiaPvtLimited', '#1877f2', '<path d="M22 12a10 10 0 10-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0022 12z"/>'],
  ['YouTube', 'https://youtube.com/user/DiabetikFootcare', '#ff0000', '<path d="M23 12s0-3.3-.4-4.8a2.5 2.5 0 00-1.8-1.8C19.3 5 12 5 12 5s-7.3 0-8.8.4A2.5 2.5 0 001.4 7.2C1 8.7 1 12 1 12s0 3.3.4 4.8a2.5 2.5 0 001.8 1.8C4.7 19 12 19 12 19s7.3 0 8.8-.4a2.5 2.5 0 001.8-1.8C23 15.3 23 12 23 12zM9.8 15.3V8.7l5.7 3.3z"/>'],
];

export default function Microsite() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [status, setStatus] = useState('loading');
  const [enquiry, setEnquiry] = useState(null); // null | { interest }

  useEffect(() => {
    fetch(`${API}/public/employee/${slug}`).then((r) => r.json()).then((d) => {
      if (d.success && d.employee) { setEmp(d.employee); setStatus('ok'); }
      else setStatus('notfound');
    }).catch(() => setStatus('notfound'));
    trackVisit(slug, 'microsite'); // CRM: log visitor + open a session
    trackEvent('page_view', 'Opened microsite', { employeeSlug: slug, source: 'microsite' });
  }, [slug]);

  if (status === 'loading') return <div className="min-h-screen flex items-center justify-center text-[#0b6e3e] font-semibold">Loading…</div>;
  if (status === 'notfound') return <div className="min-h-screen flex items-center justify-center text-gray-700">Profile not found</div>;

  const designation = (emp.designation && emp.designation.trim()) || DESIGNATIONS[(emp.email || '').toLowerCase()] || 'Team Member';
  const initials = emp.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const cardUrl = publicUrl(`/profile/${emp.profileSlug || slug}`); // QR hands over the full card
  const email = (emp.email && emp.email.trim()) || 'sales@diabetikfootcare.com';

  return (
    <div className="min-h-screen bg-slate-100 text-[#12332a]">
      {/* ---- TOP BAR ---- */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/dfci-logo.png" alt="DFC" className="w-10 h-10 object-contain shrink-0" />
            <div className="min-w-0">
              <div className="font-bold text-[#0b6e3e] leading-tight truncate">Diabetik Foot Care India</div>
              <div className="text-[11px] text-slate-500">Microsite · Pvt. Ltd.</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <a href={SITE} target="_blank" rel="noreferrer" className="hidden sm:inline text-sm font-semibold text-slate-600 hover:text-[#0b6e3e]">Home</a>
            <button onClick={() => navigate(`/profile/${slug}`)} className="text-sm font-semibold text-[#0b6e3e] hover:underline">Digital Card</button>
            {emp.isAdmin ? (
              <button onClick={() => navigate('/login')}
                className="text-sm font-bold text-white bg-[#1a8046] hover:bg-[#156637] px-4 py-2 rounded-lg shadow-sm">Admin Login</button>
            ) : (
              <button onClick={() => navigate('/employee/login')}
                className="text-sm font-bold text-white bg-[#1a8046] hover:bg-[#156637] px-4 py-2 rounded-lg shadow-sm">Emp Login</button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ================= LEFT (products + stats) ================= */}
        <div className="lg:col-span-2 space-y-6 order-2 lg:order-1">
          {/* key achievements — above the fold, before the catalogue */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {STATS.map((st) => (
              <div key={st.label} className="bg-white rounded-2xl border border-slate-100 shadow-sm px-3 py-4 text-center">
                <span className="w-11 h-11 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ background: 'linear-gradient(145deg,#e7f5ec,#cdeed9)' }}>
                  <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="#0b6e3e" strokeWidth="1.8"
                       strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: st.icon }} />
                </span>
                <div className="text-[22px] font-extrabold text-[#1a8046] leading-none">{st.big}</div>
                <div className="text-[12px] font-semibold text-[#12332a] leading-snug mt-1.5">{st.label}</div>
                <div className="text-[10.5px] text-slate-400 leading-snug mt-0.5">{st.sub}</div>
              </div>
            ))}
          </div>

          {/* about us */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h2 className="text-lg font-bold text-[#0b6e3e] mb-2">About Diabetik Foot Care India</h2>
            <p className="text-slate-600 leading-relaxed text-[15px]">
              An ISO 13485:2016 certified manufacturer, importer and distributor of diagnostic &amp; therapeutic
              products for diabetic foot care — trusted across 30+ countries for over three decades. We deliver
              accurate, reliable solutions for early detection and effective management of diabetic foot complications.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {['ISO 13485:2016', 'Govt. of India Approved', 'Since 1994', '30+ Countries'].map((c) =>
                <span key={c} className="text-xs font-semibold px-3 py-1 rounded-full bg-[#e7f5ec] text-[#0b6e3e] border border-[#b8e0c6]">{c}</span>)}
            </div>
          </div>

          {/* products */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-extrabold text-[#0b6e3e]">🏥 Our Products</h2>
              <a href={SITE} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#1a8046] hover:underline">Explore All →</a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PRODUCTS.map((p) => (
                <div key={p.name} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
                  <div className="h-40 bg-white flex items-center justify-center p-4 border-b border-slate-100">
                    {p.img ? <img src={p.img} alt={p.name} className="max-h-full max-w-full object-contain" />
                      : <div className="w-full h-full rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#e7f5ec,#cdeed9)' }}>
                          <svg viewBox="0 0 64 64" width="50" height="50" fill="none" stroke="#0b6e3e" strokeWidth="3" strokeLinecap="round">
                            <path d="M20 8c-6 0-9 5-9 10 0 6 4 8 4 14s-5 7-5 12 4 10 10 10" /><path d="M44 8c6 0 9 5 9 10 0 6-4 8-4 14s5 7 5 12-4 10-10 10" /><circle cx="32" cy="32" r="4" fill="#1a8046" stroke="none" />
                          </svg>
                        </div>}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-bold text-[15px] text-[#12332a]">{p.name}</h3>
                    <p className="text-slate-500 text-[13px] mt-1">{p.desc}</p>
                    <div className="mt-2 space-y-1">
                      {p.features.map((f) => <div key={f} className="text-[12.5px] text-slate-600 flex items-center gap-1.5"><span className="text-[#1a8046]">✓</span>{f}</div>)}
                    </div>
                    <div className="mt-3 text-center text-[12px] font-semibold text-[#0b6e3e] bg-[#e7f5ec] rounded-lg py-1.5">{p.tag}</div>
                    <div className="mt-3 space-y-2">
                      <a href={SITE} target="_blank" rel="noreferrer" onClick={() => trackEvent('product_view', `Viewed ${p.name}`, { employeeSlug: slug, source: 'microsite' })} className="block text-center text-sm font-semibold text-white bg-[#1a8046] hover:bg-[#156637] rounded-lg py-2">View Details</a>
                      <div className="flex gap-2">
                        <a href={brochureFor(p.name)} target="_blank" rel="noreferrer" onClick={() => trackEvent('brochure', `Downloaded brochure · ${p.name}`, { employeeSlug: slug, source: 'microsite' })} className="flex-1 text-center text-[13px] font-semibold text-[#0b6e3e] border border-[#b8e0c6] hover:bg-[#e7f5ec] rounded-lg py-2">⬇ Brochure</a>
                        <button onClick={() => { trackEvent('product_view', `Requested quote · ${p.name}`, { employeeSlug: slug, source: 'microsite' }); setEnquiry({ interest: p.name }); }} className="flex-1 text-center text-[13px] font-semibold text-white bg-[#0e7490] hover:bg-[#0b5566] rounded-lg py-2">Get Quote</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ================= RIGHT (profile sidebar) ================= */}
        <div className="space-y-6 order-1 lg:order-2">
          <div className="rounded-2xl p-6 text-center lg:sticky lg:top-20 text-[#eaf3ee]"
               style={{ background: 'linear-gradient(160deg,#2b2d31,#151619)', border: '1px solid rgba(201,162,75,.2)', boxShadow: '0 24px 60px rgba(0,0,0,.4)' }}>
            <div className="w-28 h-28 rounded-full overflow-hidden mx-auto bg-[#1f2125] flex items-center justify-center" style={{ boxShadow: '0 0 0 4px #c9a24b, 0 0 0 8px rgba(201,162,75,.15)' }}>
              <Photo slug={emp.profileSlug} initials={initials} big />
            </div>
            <h1 className="text-xl font-extrabold mt-3 text-white">{emp.name}</h1>
            <div className="text-[#e6c66a] font-semibold uppercase text-[13px] tracking-wide">{designation}</div>
            <p className="text-[#9fc0b2] text-[13px] mt-2">Helping you find the right diagnostic solution for diabetic foot care.</p>

            <div className="mt-4 space-y-2.5">
              {/* Single Contact Me button — same lead-capture form as the digital card.
                  Captures the visitor's phone number; no Call / WhatsApp buttons. */}
              <button type="button"
                onClick={() => { trackEvent('contact', 'Clicked Contact Me on microsite', { employeeSlug: slug, source: 'microsite' }); setEnquiry({ interest: '' }); }}
                className="w-full flex items-center justify-center gap-2 font-bold py-3 rounded-xl shadow hover:brightness-105 text-[#2a1e06]"
                style={{ background: 'linear-gradient(145deg,#f4dc95,#cba53f)', boxShadow: '0 5px 14px rgba(201,162,75,.35),inset 0 1px 0 rgba(255,255,255,.5)' }}>✉️ Contact Me</button>
              {/* Save Card — downloads the rep's details as a vCard so the customer
                  stores this card in their phone contacts (works offline afterwards). */}
              <button type="button"
                onClick={() => {
                  trackEvent('save_contact', 'Saved card (vCard) from microsite', { employeeSlug: slug, source: 'microsite' });
                  const tel = emp.whatsapp ? String(emp.whatsapp).replace(/[^\d+]/g, '') : '';
                  const vcard = [
                    'BEGIN:VCARD', 'VERSION:3.0',
                    `FN:${emp.name}`,
                    emp.designation ? `TITLE:${emp.designation}` : '',
                    `ORG:${emp.company || 'Diabetik Foot Care India'}`,
                    tel ? `TEL;TYPE=CELL:${tel}` : '',
                    emp.email ? `EMAIL:${emp.email}` : '',
                    `URL:${cardUrl}`,
                    'END:VCARD',
                  ].filter(Boolean).join('\r\n');
                  const url = URL.createObjectURL(new Blob([vcard], { type: 'text/vcard' }));
                  const a = document.createElement('a');
                  a.href = url; a.download = `${(emp.name || 'contact').replace(/\s+/g, '-')}.vcf`;
                  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                }}
                className="w-full flex items-center justify-center gap-2 font-bold py-3 rounded-xl border border-[#c9a24b]/50 text-[#eaf3ee] hover:bg-white/5">💾 Save Card</button>
              {/* QR replaces the phone/email rows — scan hands over the full card
                  (phone, email, WhatsApp, save-contact) instead of one detail at a time. */}
              <div className="flex items-center justify-center gap-3 border border-[#c9a24b]/40 rounded-xl p-2.5">
                <div className="bg-white rounded-lg p-1.5 shrink-0">
                  <QRCodeSVG value={cardUrl} size={72} level="M" />
                </div>
                <div className="text-left">
                  <div className="text-[13px] font-bold text-[#eaf3ee] leading-tight">Scan for my card</div>
                  <div className="text-[11.5px] text-[#9fc0b2] leading-snug mt-0.5">Phone, email and WhatsApp — straight to your phone.</div>
                </div>
              </div>
            </div>

            <div className="flex justify-center gap-3 mt-5">
              {SOCIALS.map(([label, href, color, path]) => (
                // LinkedIn points at the rep's own profile when they have one; the
                // rest stay on the company accounts, as does LinkedIn without it.
                <a key={label} href={label === 'LinkedIn' && emp.linkedin ? emp.linkedin : href}
                   target="_blank" rel="noreferrer" title={label} aria-label={label}
                   className="w-9 h-9 rounded-lg flex items-center justify-center hover:brightness-110 transition" style={{ background: color }}>
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="#fff" dangerouslySetInnerHTML={{ __html: path }} />
                </a>
              ))}
            </div>
          </div>

          {/* about me */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h2 className="text-lg font-bold text-[#0b6e3e] mb-2">About Me</h2>
            <p className="text-slate-600 text-[14px] leading-relaxed">
              {emp.name.split(' ')[0]} is part of the Diabetik Foot Care India team, helping clinics and hospitals
              adopt the right diagnostic and therapeutic solutions for diabetic foot care.
            </p>
            <div className="mt-4 flex items-center gap-3 bg-[#fff8e6] border border-[#f0e0a8] rounded-xl p-3">
              <span className="text-2xl">🎓</span>
              <div><div className="font-bold text-[#8a6d1a] text-sm">Certifications</div><div className="text-[12px] text-[#8a6d1a]">ISO 13485:2016 · Govt. of India Approved</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- FOOTER ---- */}
      <div className="bg-[#0e2a20] text-slate-300 mt-4">
        <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
          <div>
            <div className="text-[#e6c66a] font-bold mb-2">Company Info</div>
            Diabetik Foot Care India Pvt. Ltd.<br />No.18/1, Kannappa Nagar, 3rd Main Road,<br />Thiruvanmiyur, Chennai – 600041
          </div>
          <div>
            <div className="text-[#e6c66a] font-bold mb-2">Contact</div>
            <a href="mailto:sales@diabetikfootcare.com" className="hover:text-white">sales@diabetikfootcare.com</a><br />
            +91 93806 21606
          </div>
          <div>
            <div className="text-[#e6c66a] font-bold mb-2">Quick Links</div>
            <a href={SITE} target="_blank" rel="noreferrer" className="hover:text-white">Home</a> ·{' '}
            <button onClick={() => navigate(`/profile/${slug}`)} className="hover:text-white">Digital Card</button> ·{' '}
            <button onClick={() => navigate(emp.isAdmin ? '/login' : '/employee/login')} className="hover:text-white">{emp.isAdmin ? 'Admin Login' : 'Emp Login'}</button>
          </div>
        </div>
        <div className="text-center text-[11px] text-slate-500 pb-6">© 2026 Diabetik Foot Care India Pvt. Ltd. · Powered by TECGSOFT</div>
      </div>

      {enquiry && <ContactModal slug={slug} source="microsite" handoff={false} presetInterest={enquiry.interest} onClose={() => setEnquiry(null)} />}
    </div>
  );
}

function Photo({ slug, initials, big }) {
  const exts = ['jpg', 'jpeg', 'png', 'webp'];
  const [i, setI] = useState(0);
  if (!slug || i >= exts.length) return <span className={`text-white font-bold ${big ? 'text-3xl' : 'text-2xl'}`}>{initials}</span>;
  return <img src={`/photos/${slug}.${exts[i]}`} alt={initials} onError={() => setI(i + 1)} className="w-full h-full object-cover" />;
}
